<template>
  <div class="machines-show-matrix container-fluid">
    <h2 class="page-heading">Матрица {{matrix | get('name')}}</h2>

    <transition name="fade">
      <div class="row">
        <div class="col-md">
          <div v-if="errorMessage" class="alert alert-danger" role="alert">
            {{ errorMessage }}
          </div>

          <div v-if="totalSum > maxAmount" class="alert alert-warning" role="alert">
            Максимальная сумма отправки: {{ maxAmount }}.
          </div>

          <div v-if="receiptsSent" class="alert alert-success" role="alert">
            Чеки отправлены...
          </div>

          <div class="card mb-4" v-else-if="$root.checkPermission('web_matrix_send_receipts')">
            <h3 class="card-header"><span class="text-caps">Доотправка чеков</span></h3>
            <div class="card-body">
              <table class="table-sm m-0 px-0 w-100">
                <thead>
                <tr>
                  <th>Продукт</th>
                  <th class="text-right">Количество</th>
                  <th class="text-right">Цена</th>
                  <th class="text-right">Сумма</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(product, index) in productCopies" :key="index">
                  <td>{{ product.product_name }}</td>
                  <td>
                    <input 
                      v-model.number="product.product_quantity" 
                      type="number" 
                      min="0" 
                      max="99" 
                      class="form-control form-control-sm text-right" 
                      :disabled="isSubmitting"
                    />
                  </td>
                  <td>
                    <input 
                      v-model.number="product.product_cost" 
                      type="number" 
                      min="0" 
                      max="999" 
                      class="form-control form-control-sm text-right" 
                      :disabled="isSubmitting"
                    />
                  </td>
                  <td class="text-right">{{ ((product.product_quantity || 0) * (product.product_cost || 0)).toFixed(2) }}</td>
                </tr>
                </tbody>
                <tfoot>
                <tr>
                  <td colspan="3" class="text-right"><strong>Итоговая сумма:</strong></td>
                  <td class="text-right"><strong>{{ totalSum.toFixed(2) }}</strong></td>
                </tr>
                </tfoot>
              </table>
              <button 
                @click="confirmReceipts" 
                class="btn btn-primary mt-3" 
                :disabled="totalSum > maxAmount || isSubmitting"
              >Создать чеки</button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import api from '@/api'

  export default {
    props: {
      id: {required: true},
      machine: {type: Object},
    },
    data () {
      return {
        productCopies: [], // Для хранения копии массива с продуктами
        receiptsSent: false, // Управляет отображением таблицы или сообщения об успешной отправке
        maxAmount: 50000, // Максимальная допустимая сумма для отправки чеков
        maxQuantity: 99,  // Максимальное значение для количества
        maxCost: 999,  // Максимальное значение для цены
        isSubmitting: false, // Управляет состоянием блокировки полей и кнопки
        errorMessage: null,  // Сообщение об ошибке
      };
    },
    computed: {
      matrix () {
        return this.machine.matrix;
      },
      // Вычисляем итоговую сумму всех продуктов
      totalSum() {
        return this.productCopies.reduce((sum, product) => {
          return sum + ((product.product_quantity || 0) * (product.product_cost || 0));
        }, 0);
      }
    },
    watch: {
      // Отслеживаем изменения количества и цены каждого продукта
      productCopies: {
        handler: function (newVal) {
          newVal.forEach(product => {
            // Если количество больше максимального, устанавливаем его на максимум
            if (product.product_quantity > this.maxQuantity) {
              product.product_quantity = this.maxQuantity;
            }
            // Если количество меньше минимального, устанавливаем его на 0
            if (product.product_quantity < 0) {
              product.product_quantity = 0;
            }
            // Если цена больше максимальной, устанавливаем её на максимум
            if (product.product_cost > this.maxCost) {
              product.product_cost = this.maxCost;
            }
            // Если цена меньше минимальной, устанавливаем её на 0
            if (product.product_cost < 0) {
              product.product_cost = 0;
            }
          });
        },
        deep: true, // Нужно отслеживать изменения вложенных объектов
      },
    },
    methods: {
      // Метод для создания копии массива продуктов
      initializeProductCopies() {
        const uniqueProducts = new Map();

        // Проходим по продуктам и добавляем только уникальные по имени
        this.machine.matrix.products.forEach(product => {
          if (product.product !== null && !uniqueProducts.has(product.product.name)) {
            uniqueProducts.set(product.product.name, {
              product_id: product.product_id,
              product_name: product.product.name,
              product_quantity: 0,
              product_cost: 0,
            });
          }
        });

        // Преобразуем Map в массив и присваиваем его productCopies
        this.productCopies = Array.from(uniqueProducts.values());

        this.fetchLastEva();
      },
      // Запрос к API для получения последнего EvaDtsRaw
      fetchLastEva() {
        api.get(`machines/${this.machine.id}/lasteva`)
          .then(response => {
            const eva = response.data.eva;
            if (eva) {
              this.updateProductPrices(eva);
            }
          })
          .catch(error => {
            this.errorMessage = `Ошибка при получении данных Eva: ${error.response && error.response.data && error.response.data.message ? error.response.data.message : error.message}`;
          });
      },
      // Обновление цен в productCopies на основе данных из ответа
      updateProductPrices(evaProducts) {
        evaProducts.forEach(evaProduct => {
          // Поиск продукта по ID или имени
          const product = this.productCopies.find(p => 
            p.product_id === evaProduct.id || p.product_name === evaProduct.id
          );

          if (product) {
            // Присваиваем цену только в виде целого числа
            product.product_cost = parseInt(evaProduct.real_price, 10) || 0;
          }
        });
      },
      // Метод для отображения подтверждения перед отправкой
      confirmReceipts() {
        const selectedProducts = this.productCopies.filter(product => product.product_quantity > 0);

        if (selectedProducts.length === 0) {
          alert("Вы не выбрали продукты для отправки чеков.");
          return;
        }

        const confirmationMessage = selectedProducts.map(product => {
          return `Продукт: ${product.product_name}, Количество: ${product.product_quantity}, Цена: ${product.product_cost}\n`;
        }).join('');

        if (confirm(`Подтвердите отправку чеков для следующих продуктов:\n\n${confirmationMessage}`)) {
          this.createReceipts();
        }
      },
      // Обработчик для создания чеков
      createReceipts() {
        this.isSubmitting = true;
        this.errorMessage = null;

        const data = {
          machine: {
            id: this.machine.id
          },
          receipts: this.productCopies
            .filter(product => product.product_quantity > 0)
            .map(product => ({
              id: product.product_id,
              name: product.product_name,
              quantity: product.product_quantity,
              price: product.product_cost,
            })),
        };

        api.post(`receipts/matrix/send`, data)
          .then(() => {
            console.log('успешно отправлено');
            this.errorMessage = null;
            this.receiptsSent = true;
          })
          .catch(error => {
            this.isSubmitting = false;
            this.errorMessage = `Ошибка при отправке чеков: ${error.response && error.response.data && error.response.data.message ? error.response.data.message : error.message}`;
          });
      }
    },
    mounted() {
      this.initializeProductCopies();
    }
  }
</script>

<style lang="scss">
  @import "boot";

  .machines-show-matrix {
  }
</style>



// WEBPACK FOOTER //
// resources/assets/js/pages/machines/_id/matrixSendReceipts.vue